import { cn } from "@/lib/utils";
import { Calendar, AlignLeft, AlertCircle, Zap, UserCheck, Archive, Calendar as CalIcon } from "lucide-react";
import { format } from "date-fns";

const LABEL_COLORS = {
  indigo: "bg-blue-500", blue: "bg-blue-500", violet: "bg-slate-600", slate: "bg-slate-600",
  pink: "bg-sky-500", emerald: "bg-emerald-500", amber: "bg-amber-500",
  sky: "bg-sky-500", rose: "bg-rose-500", red: "bg-red-500",
};

// Priority chip derived from the urgent/important matrix.
function deriveQuadrant(card) {
  const u = !!card.is_urgent;
  const i = !!card.is_important;
  if (u && i) return { id: "do",       label: "Hacer ahora", icon: Zap,
    cls: "bg-rose-500/15 text-rose-600 dark:text-rose-300" };
  if (!u && i) return { id: "plan",     label: "Programar",  icon: CalIcon,
    cls: "bg-blue-500/15 text-blue-600 dark:text-blue-300" };
  if (u && !i) return { id: "delegate", label: "Delegar",    icon: UserCheck,
    cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300" };
  return null; // !urgent && !important → no chip (posponer is implicit)
}

export default function CardTile({ card, labels = [], onClick, meta }) {
  const cardLabels = labels.filter(l => (card.labels || []).includes(l.label_id));
  const due = card.due_date ? new Date(card.due_date) : null;
  const isOverdue = due && due < new Date();
  const quadrant = deriveQuadrant(card);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(); } }}
      data-testid={`card-tile-${card.card_id}`}
      className="w-full text-left rounded-xl bg-white/80 dark:bg-slate-800/70 backdrop-blur border border-white/60 dark:border-white/10 shadow-sm p-3 hover:-translate-y-0.5 hover:shadow-md transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
    >
      {cardLabels.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {cardLabels.map(l => (
            <span key={l.label_id} className={cn("h-1.5 w-8 rounded-full", LABEL_COLORS[l.color] || "bg-blue-500")} title={l.name} />
          ))}
        </div>
      )}
      <p className="text-sm font-medium leading-snug line-clamp-3">{card.title}</p>
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        {quadrant && (
          <span className={cn("inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md uppercase tracking-wider font-semibold", quadrant.cls)}>
            <quadrant.icon className="h-3 w-3" />
            {quadrant.label}
          </span>
        )}
        {due && (
          <span className={cn(
            "inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md",
            isOverdue ? "bg-rose-500/15 text-rose-600 dark:text-rose-300" : "bg-slate-500/15 text-slate-600 dark:text-slate-300"
          )}>
            {isOverdue ? <AlertCircle className="h-3 w-3" /> : <Calendar className="h-3 w-3" />}
            {format(due, "d MMM")}
          </span>
        )}
        {card.description && (
          <span className="inline-flex items-center text-[11px] text-muted-foreground"><AlignLeft className="h-3 w-3" /></span>
        )}
        {meta && (
          <span className="ml-auto text-[10px] text-muted-foreground truncate max-w-[120px]" title={meta}>
            · {meta}
          </span>
        )}
      </div>
    </div>
  );
}
